<template>
  <v-card class="dialog-card">
    <ModalHeader
      icon="mdi-delete-restore"
      modalTitle="Deleted Assets"
      modalSubtitle="Restore or permanently delete assets"
      @close="emit('close')" />

    <!-- Search and Count -->
    <div class="px-4 mb-2 d-flex align-center">
      <v-text-field
        v-model="searchQuery"
        density="compact"
        append-inner-icon="mdi-magnify"
        single-line
        hide-details
        variant="outlined"
        placeholder="Search deleted assets..."
        rounded
        style="max-width: 300px"
        @input="debouncedSearch" />
      <v-spacer />
      <v-chip
        v-if="totalCount > 0"
        size="small"
        color="error"
        variant="tonal">
        {{ totalCount }} deleted
      </v-chip>
    </div>

    <!-- Deleted Assets List -->
    <div
      class="deleted-assets-container"
      style="max-height: 500px; overflow-y: auto">
      <div
        v-if="loading"
        class="d-flex justify-center align-center pa-8">
        <v-progress-circular indeterminate />
      </div>

      <div
        v-else-if="Object.keys(deletedAssets).length === 0"
        class="text-center pa-8">
        <v-icon
          size="64"
          color="grey-lighten-1"
          class="mb-4">
          mdi-delete-empty
        </v-icon>
        <p class="text-grey">No deleted assets found</p>
      </div>

      <div
        v-else
        class="pa-4">
        <v-card
          v-for="asset in sortedAssets"
          :key="asset.uid"
          class="mb-3 deleted-asset-card"
          variant="flat"
          style="background-color: var(--inner-background); border-radius: 5px">
          <div class="d-flex align-center pa-3">
            <!-- Thumbnail -->
            <div
              class="asset-thumb mr-3"
              style="width: 60px; height: 60px; flex-shrink: 0">
              <img
                v-if="asset.preview_static_small"
                :src="getThumbnailUrl(asset)"
                style="
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  border-radius: 4px;
                "
                @error="handleImgError" />
              <v-icon
                v-else
                size="40"
                color="grey"
                style="
                  width: 100%;
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: rgba(0, 0, 0, 0.05);
                  border-radius: 4px;
                ">
                {{ getAssetIcon(asset.asset_type) }}
              </v-icon>
            </div>

            <!-- Asset Info -->
            <div
              class="flex-grow-1"
              style="min-width: 0">
              <div
                class="font-weight-medium text-truncate"
                style="max-width: 300px">
                {{ asset.name }}
              </div>
              <div class="text-caption text-grey d-flex align-center">
                <v-icon
                  size="12"
                  class="mr-1">
                  mdi-clock-outline
                </v-icon>
                Deleted {{ formatDeletedTime(asset.deleted_timestamp) }}
              </div>
              <div class="text-caption text-grey">
                <v-icon
                  size="12"
                  class="mr-1">
                  mdi-file-outline
                </v-icon>
                {{ asset.asset_type }}
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex">
              <v-btn
                :loading="restoring[asset.uid]"
                :disabled="deleting[asset.uid]"
                color="success"
                variant="tonal"
                size="small"
                class="mr-2"
                @click="restoreAsset(asset)"
                title="Restore asset">
                <v-icon>mdi-restore</v-icon>
              </v-btn>
              <v-btn
                :loading="deleting[asset.uid]"
                :disabled="restoring[asset.uid]"
                color="error"
                variant="tonal"
                size="small"
                @click="confirmPermanentDelete(asset)"
                title="Permanently delete">
                <v-icon>mdi-delete-forever</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- Load More -->
    <div
      v-if="hasMore && !loading"
      class="d-flex justify-center pa-4">
      <v-btn
        variant="outlined"
        @click="loadMore"
        :loading="loadingMore">
        Load More
      </v-btn>
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      <v-btn
        v-if="totalCount > 0"
        color="error"
        variant="tonal"
        @click="confirmDeleteAllDialog = true"
        title="Permanently delete all deleted assets">
        <v-icon class="mr-2">mdi-delete-sweep</v-icon>
        Delete All
      </v-btn>
      <v-spacer />
      <v-btn
        @click="emit('close')"
        style="background-color: var(--inner-background)"
        variant="flat">
        Close
      </v-btn>
    </div>

    <!-- Confirm Permanent Delete Dialog -->
    <v-dialog
      v-model="confirmDeleteDialog"
      max-width="400">
      <v-card
        v-if="confirmDeleteDialog"
        class="dialog-card">
        <div class="pa-3">
          <ModalHeader
            icon="mdi-alert"
            modalTitle="Permanent Delete"
            @close="confirmDeleteDialog = false" />
        </div>
        <div
          class="mx-3 pa-3"
          style="background: rgba(0, 0, 0, 0.06); border-radius: 5px">
          <v-card-text>
            <p>
              <strong>{{ assetToDelete?.name }}</strong>
            </p>
            <p class="text-error mt-2">
              This will permanently delete the asset file and all associated
              thumbnails and optimizations. This action cannot be undone!
            </p>
          </v-card-text>
        </div>
        <div class="modal-footer">
          <v-btn
            @click="confirmDeleteDialog = false"
            variant="flat">
            <v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            @click="permanentDelete"
            :loading="deletingPermanent"
            color="error"
            variant="flat">
            <v-icon class="mr-2">mdi-delete-forever</v-icon>
            Delete Permanently
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete All Dialog -->
    <v-dialog
      v-model="confirmDeleteAllDialog"
      max-width="400">
      <v-card
        v-if="confirmDeleteAllDialog"
        class="dialog-card">
        <div class="pa-3">
          <ModalHeader
            icon="mdi-alert"
            modalTitle="Delete All"
            @close="confirmDeleteAllDialog = false" />
        </div>
        <div
          class="mx-3 pa-3"
          style="background: rgba(0, 0, 0, 0.06); border-radius: 5px">
          <v-card-text>
            <p>
              <strong>{{ totalCount }} asset(s)</strong> will be permanently deleted.
            </p>
            <p class="text-error mt-2">
              This will permanently delete all asset files and their associated
              thumbnails and optimizations. This action cannot be undone!
            </p>
          </v-card-text>
        </div>
        <div class="modal-footer">
          <v-btn
            @click="confirmDeleteAllDialog = false"
            variant="flat">
            <v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            @click="permanentDeleteAll"
            :loading="deletingAll"
            color="error"
            variant="flat">
            <v-icon class="mr-2">mdi-delete-sweep</v-icon>
            Delete All Permanently
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { $http } from '../../stores/http';
import { state } from '../../stores/deluge';
import ModalHeader from '../Shared/Modals/ModalHeader.vue';
import { iconByType } from '../../utils/assetTypeToMDIIcon';

const emit = defineEmits(['close', 'restored']);

const deletedAssets = ref({});
const loading = ref(true);
const loadingMore = ref(false);
const searchQuery = ref('');
const offset = ref(0);
const hasMore = ref(true);
const totalCount = computed(() => Object.keys(deletedAssets.value).length);
const restoring = ref({});
const deleting = ref({});

// Confirm delete dialog
const confirmDeleteDialog = ref(false);
const assetToDelete = ref(null);
const deletingPermanent = ref(false);

// Confirm delete all dialog
const confirmDeleteAllDialog = ref(false);
const deletingAll = ref(false);

const sortedAssets = computed(() => {
  return Object.values(deletedAssets.value).sort((a, b) => a.index - b.index);
});

onMounted(async () => {
  await loadDeletedAssets();
});

async function loadDeletedAssets(append = false) {
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
  }

  try {
    const params = new URLSearchParams({
      limit: '30',
      offset: append ? offset.value.toString() : '0',
    });

    if (searchQuery.value) {
      params.append('search_query', searchQuery.value);
    }

    const response_raw = await $http('GET', `/get_deleted_assets?${params}`);
    const response = JSON.parse(response_raw);

    if (append) {
      deletedAssets.value = { ...deletedAssets.value, ...response };
    } else {
      deletedAssets.value = response;
      offset.value = 0;
    }

    // Update offset for next load
    offset.value = Object.keys(deletedAssets.value).length;

    // Check if there are more items
    hasMore.value = Object.keys(response).length >= 30;
  } catch (error) {
    console.error('Failed to load deleted assets:', error);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

function loadMore() {
  loadDeletedAssets(true);
}

let searchTimeout = null;
function debouncedSearch() {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    loadDeletedAssets(false);
  }, 300);
}

async function restoreAsset(asset) {
  restoring.value[asset.uid] = true;
  try {
    await $http('PUT', `/restore_asset?asset_uid=${asset.uid}`);

    // Remove from deleted list
    delete deletedAssets.value[asset.uid];

    // Emit restored event
    emit('restored', asset);
  } catch (error) {
    console.error('Failed to restore asset:', error);
  } finally {
    restoring.value[asset.uid] = false;
  }
}

function confirmPermanentDelete(asset) {
  assetToDelete.value = asset;
  confirmDeleteDialog.value = true;
}

async function permanentDelete() {
  if (!assetToDelete.value) return;

  const asset = assetToDelete.value;
  deletingPermanent.value = true;

  try {
    await $http(
      'DELETE',
      `/delete_asset?asset_uid=${asset.uid}&permanent=true`
    );

    // Remove from deleted list
    delete deletedAssets.value[asset.uid];

    confirmDeleteDialog.value = false;
    assetToDelete.value = null;
  } catch (error) {
    console.error('Failed to permanently delete asset:', error);
  } finally {
    deletingPermanent.value = false;
  }
}

async function permanentDeleteAll() {
  deletingAll.value = true;
  try {
    await $http('DELETE', '/clear_expired_assets');
    deletedAssets.value = {};
    confirmDeleteAllDialog.value = false;
  } catch (error) {
    console.error('Failed to permanently delete all assets:', error);
  } finally {
    deletingAll.value = false;
  }
}

function getThumbnailUrl(asset) {
  return `${state.devRedirect}/thumbnails/previews/static/small/${asset.uid}.jpg`;
}

function handleImgError(event) {
  event.target.style.display = 'none';
}

function getAssetIcon(assetType) {
  return iconByType(assetType);
}

function formatDeletedTime(timestamp) {
  if (!timestamp) return 'Unknown';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
}
</script>

<style scoped>
.deleted-assets-container {
  min-height: 200px;
}

.deleted-asset-card {
  transition: all 0.2s ease;
}

.deleted-asset-card:hover {
  filter: brightness(1.15);
}

.asset-thumb img {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
